import React, { useState, useEffect } from 'react';
import { Menu, X, LogOut, User as UserIcon, Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { navLinks } from '../../mock';
import { useAuth } from '../../lib/AuthContext';
import { useSubscription } from '../../lib/SubscriptionContext';

// Small pill next to the user's email showing their current plan.
// Reads from SubscriptionContext (same source as the publish gate),
// so what the badge says is what publishing actually allows.
const PLAN_STYLES = {
  free:       { label: 'Free',       bg: '#F3F4F6', fg: '#4B5563', border: '#E5E7EB' },
  pro:        { label: 'Pro',        bg: '#EEF2FF', fg: 'var(--yb-indigo)', border: '#C7D2FE' },
  enterprise: { label: 'Enterprise', bg: '#FEF3C7', fg: '#92400E', border: '#FDE68A' },
  owner:      { label: 'Owner',      bg: '#ECFDF5', fg: '#065F46', border: '#A7F3D0' },
};

const PlanBadge = ({ plan, loading, size = 'sm' }) => {
  if (loading && !plan) return null;
  const key = (plan || 'free').toLowerCase();
  const s = PLAN_STYLES[key] || PLAN_STYLES.free;
  const px = size === 'sm' ? 'px-2 py-0.5 text-[10.5px]' : 'px-2.5 py-1 text-[11.5px]';
  return (
    <span
      data-testid={`plan-badge-${key}`}
      className={`inline-flex items-center rounded-full font-semibold uppercase tracking-wider border ${px}`}
      style={{ backgroundColor: s.bg, color: s.fg, borderColor: s.border }}
    >
      {s.label}
    </span>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const { user, signOut } = useAuth();
  const { plan, loading: planLoading } = useSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setMenu(false);
    navigate('/');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-[#FBFAF7]/85 backdrop-blur-md border-b border-[#ECEAE3]' : 'bg-transparent'
      }`}
      style={{ color: 'var(--yb-ink)' }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" style={{ color: 'var(--yb-ink)' }}>
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => {
            const cls = "px-4 py-2 text-[14.5px] rounded-full transition-colors";
            const commonProps = {
              className: cls,
              style: { color: 'var(--yb-text)' },
              onMouseEnter: (e) => (e.currentTarget.style.color = 'var(--yb-indigo)'),
              onMouseLeave: (e) => (e.currentTarget.style.color = 'var(--yb-text)'),
            };
            // Every nav destination is an internal SPA route (routes or
            // `/#anchor` on the homepage). Use React Router's <Link> so
            // hash anchors keep working when the user is on a route
            // other than `/`, and the App-level scroll-to-hash effect
            // gets a chance to run after the homepage renders.
            return <Link key={l.label} to={l.href} {...commonProps}>{l.label}</Link>;
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {!user && (
            <>
              <Link
                to="/login"
                className="px-4 py-2 text-[14.5px] transition-colors"
                style={{ color: 'var(--yb-text)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--yb-indigo)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--yb-text)')}
              >
                Log in
              </Link>
              <Link to="/signup" className="yb-btn-primary px-4 py-2 text-[14.5px] font-semibold rounded-full">
                Get started
              </Link>
            </>
          )}
          {user && (
            <div className="relative">
              <button
                data-testid="nav-account-trigger"
                onClick={() => setMenu((m) => !m)}
                className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border bg-white text-[13.5px]"
                style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
              >
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[11px]" style={{ backgroundColor: 'var(--yb-indigo)' }}>
                  {(user.email || '?').charAt(0).toUpperCase()}
                </span>
                <span className="max-w-[140px] truncate">{user.email}</span>
                <PlanBadge plan={plan} loading={planLoading} />
              </button>
              {menu && (
                <div
                  onMouseLeave={() => setMenu(false)}
                  className="absolute right-0 mt-2 w-64 bg-white rounded-xl border shadow-lg overflow-hidden"
                  style={{ borderColor: 'var(--yb-border)' }}
                >
                  {/* Plan summary strip at the top of the dropdown */}
                  <div
                    className="px-4 py-3 border-b flex items-center justify-between gap-2"
                    style={{ borderColor: 'var(--yb-border)', backgroundColor: '#FBFAF7' }}
                    data-testid="nav-account-plan-summary"
                  >
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>Current plan</span>
                      <PlanBadge plan={plan} loading={planLoading} size="md" />
                    </div>
                    {(!plan || plan === 'free') && (
                      <Link
                        to="/#pricing"
                        onClick={() => setMenu(false)}
                        data-testid="nav-account-upgrade-link"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold yb-btn-primary"
                      >
                        <Sparkles size={12} /> Upgrade
                      </Link>
                    )}
                  </div>
                  <Link to="/projects" onClick={() => setMenu(false)} className="block px-4 py-2.5 text-[13.5px] hover:bg-neutral-50" style={{ color: 'var(--yb-text)' }}>
                    <UserIcon size={13} className="inline mr-2" /> My projects
                  </Link>
                  <button onClick={handleSignOut} className="w-full text-left px-4 py-2.5 text-[13.5px] hover:bg-neutral-50" style={{ color: 'var(--yb-text)' }}>
                    <LogOut size={13} className="inline mr-2" /> Log out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <button className="lg:hidden p-2" style={{ color: 'var(--yb-ink)' }} onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FBFAF7] border-t border-[#ECEAE3] px-6 py-4">
          <div className="flex flex-col gap-1">
            {navLinks.map((l) => {
              const cls = "py-2 text-[15px]";
              const style = { color: 'var(--yb-text)' };
              return <Link key={l.label} to={l.href} className={cls} style={style} onClick={() => setOpen(false)}>{l.label}</Link>;
            })}
            <div className="flex gap-2 pt-3 border-t border-[#ECEAE3] mt-2">
              {!user ? (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center py-2 text-[14.5px]" style={{ color: 'var(--yb-text)' }}>Log in</Link>
                  <Link to="/signup" onClick={() => setOpen(false)} className="yb-btn-primary flex-1 text-center py-2 text-[14.5px] font-semibold rounded-full">Get started</Link>
                </>
              ) : (
                <div className="w-full flex flex-col gap-2">
                  <div
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg"
                    style={{ backgroundColor: '#FBFAF7', border: '1px solid var(--yb-border)' }}
                    data-testid="nav-mobile-plan-summary"
                  >
                    <PlanBadge plan={plan} loading={planLoading} />
                    {(!plan || plan === 'free') && (
                      <Link
                        to="/#pricing"
                        onClick={() => setOpen(false)}
                        data-testid="nav-mobile-upgrade-link"
                        className="text-[12.5px] font-semibold"
                        style={{ color: 'var(--yb-indigo)' }}
                      >
                        Upgrade →
                      </Link>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Link to="/projects" onClick={() => setOpen(false)} className="flex-1 text-center py-2 text-[14.5px]" style={{ color: 'var(--yb-text)' }}>My projects</Link>
                    <button onClick={handleSignOut} className="flex-1 text-center py-2 text-[14.5px]" style={{ color: 'var(--yb-text)' }}>Log out</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
